// Antwortautomatik (answer_rules) – portiert aus frontend/alt/wartungen/kategorie-einstellungen.js
// (normalizeAnswerRule, normalizeAnswerRuleCondition, normalizeAnswerRuleConfig),
// erweitert um `source_reset_value` (Rücksprung: manuelle Abweichung am Zielfeld
// setzt das Quellfeld der Regel zurück).

import {
  coerceBlankBooleanRuleValue,
  coerceRuleValueForOperator,
  normalizeOperator,
  normalizeSourceScope,
  normalizeVisibilityRulesForSave,
  type BehaviorRuleOperator,
  type BehaviorSourceScope,
} from "./behaviorRules";

export const ANSWER_RULE_TYPES = ["setAnswerIf", "setAnswerIfAll"] as const;
export type AnswerRuleType = (typeof ANSWER_RULE_TYPES)[number];

export const ANSWER_RULE_TYPE_LABELS: Record<AnswerRuleType, string> = {
  setAnswerIf: "Antwort setzen wenn",
  setAnswerIfAll: "Antwort setzen wenn alle Bedingungen erfüllt sind",
};

export const ANSWER_RULE_OVERWRITE_MODES = ["force", "manual"] as const;
export type AnswerRuleOverwriteMode = (typeof ANSWER_RULE_OVERWRITE_MODES)[number];

export const ANSWER_RULE_OVERWRITE_MODE_LABELS: Record<AnswerRuleOverwriteMode, string> = {
  force: "Regel gewinnt (automatisch setzen)",
  manual: "Manuell überschreibbar (nur wenn leer)",
};

/** Feldtypen, für die die Antwortautomatik unterstützt wird (siehe Backend ANSWER_RULE_SUPPORTED_FIELD_TYPES). */
export const ANSWER_RULE_SUPPORTED_FIELD_TYPES = new Set([
  "select",
  "choice",
  "multiselect",
  "bool",
  "checkbox",
  "article_ref_list",
]);

export interface AnswerRuleCondition {
  source_scope: BehaviorSourceScope;
  source_field_key: string;
  operator: BehaviorRuleOperator;
  value: unknown;
}

export interface AnswerRule {
  rule_type: AnswerRuleType;
  priority: number;
  conditions: AnswerRuleCondition[];
  target_value: unknown;
  /** Rücksprung: Wert für das Quellfeld der ersten Bedingung bei manueller Abweichung (Key fehlt = kein Rücksprung). */
  source_reset_value?: unknown;
}

export interface AnswerRuleConfig {
  overwrite_mode: AnswerRuleOverwriteMode;
  clear_when_not_matching: boolean;
}

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

export function normalizeAnswerRuleConfig(raw: unknown): AnswerRuleConfig {
  const source = asRecord(raw) ?? {};
  const overwriteMode = String(source.overwrite_mode || "").trim();
  return {
    overwrite_mode: (ANSWER_RULE_OVERWRITE_MODES as readonly string[]).includes(overwriteMode)
      ? (overwriteMode as AnswerRuleOverwriteMode)
      : "force",
    clear_when_not_matching: source.clear_when_not_matching !== false,
  };
}

/**
 * Bedingung normalisieren. Im Editor bleiben unvollständige Bedingungen
 * (leerer source_field_key) erhalten, damit neu angelegte Zeilen nicht
 * verschwinden; beim Speichern werden sie über `pruneAnswerRulesForSave`
 * entfernt.
 */
export function normalizeAnswerRuleCondition(raw: unknown): AnswerRuleCondition | null {
  const source = asRecord(raw);
  if (!source) return null;
  return {
    source_scope: normalizeSourceScope(source.source_scope),
    source_field_key: String(source.source_field_key || "").trim(),
    operator: normalizeOperator(source.operator),
    value: source.value ?? "",
  };
}

export function normalizeAnswerRule(raw: unknown): AnswerRule | null {
  const source = asRecord(raw);
  if (!source) return null;
  const ruleTypeRaw = String(source.rule_type || "").trim();
  const ruleType: AnswerRuleType = (ANSWER_RULE_TYPES as readonly string[]).includes(ruleTypeRaw)
    ? (ruleTypeRaw as AnswerRuleType)
    : "setAnswerIf";
  const priorityRaw = Number.parseInt(String(source.priority ?? ""), 10);
  const conditionsRaw = Array.isArray(source.conditions) ? source.conditions : [source];
  const conditions = conditionsRaw
    .map((entry) => normalizeAnswerRuleCondition(entry))
    .filter((entry): entry is AnswerRuleCondition => entry != null);
  const rule: AnswerRule = {
    rule_type: ruleType,
    priority: Number.isInteger(priorityRaw) ? priorityRaw : 100,
    conditions,
    target_value: source.target_value ?? "",
  };
  if (Object.hasOwn(source, "source_reset_value")) {
    rule.source_reset_value = source.source_reset_value;
  }
  return rule;
}

export function getAnswerRulesFromBehavior(behavior: unknown): AnswerRule[] {
  const source = asRecord(behavior) ?? {};
  const rules = Array.isArray(source.answer_rules) ? source.answer_rules : [];
  return rules
    .map((entry) => normalizeAnswerRule(entry))
    .filter((entry): entry is AnswerRule => entry != null);
}

/**
 * Für den Save-Payload: unvollständige Bedingungen und Regeln ohne gültige
 * Bedingung entfernen (Parität zum Alt-Frontend readAnswerRulesFromForm).
 */
/**
 * Behavior-JSON für den Save-Payload aufbereiten: Sichtbarkeits- und Antwortregeln
 * bereinigen und Konfiguration normalisieren.
 */
export interface BehaviorSourceTypeField {
  scope?: string;
  field_key?: string;
  type?: string;
}

function sourceTypeForFields(fields: BehaviorSourceTypeField[] | undefined) {
  const byKey = new Map<string, string>();
  for (const field of fields || []) {
    const key = String(field.field_key || "").trim();
    if (!key) continue;
    const scope = normalizeSourceScope(field.scope);
    byKey.set(`${scope}:${key}`, String(field.type || ""));
  }
  return (scope: string, fieldKey: string) => byKey.get(`${normalizeSourceScope(scope)}:${fieldKey}`);
}

export function normalizeBehaviorForSave(
  behavior: unknown,
  fields?: BehaviorSourceTypeField[],
): Record<string, unknown> {
  const obj = asRecord(behavior) ? { ...(behavior as Record<string, unknown>) } : {};
  const sourceTypeFor = sourceTypeForFields(fields);
  obj.rules = normalizeVisibilityRulesForSave(obj.rules, sourceTypeFor);
  if (Object.hasOwn(obj, "answer_rules")) {
    obj.answer_rules = pruneAnswerRulesForSave(obj.answer_rules, sourceTypeFor);
    obj.answer_rule_config = normalizeAnswerRuleConfig(obj.answer_rule_config);
  }
  return obj;
}

export function pruneAnswerRulesForSave(
  rules: unknown,
  sourceTypeFor?: (scope: string, fieldKey: string) => string | null | undefined,
): AnswerRule[] {
  const list = Array.isArray(rules) ? rules : [];
  return list
    .map((entry) => normalizeAnswerRule(entry))
    .filter((entry): entry is AnswerRule => entry != null)
    .map((rule) => {
      const effectiveConditions = (rule.rule_type === "setAnswerIfAll" ? rule.conditions : rule.conditions.slice(0, 1))
        .filter((condition) => condition.source_field_key)
        .map((condition) => ({
          ...condition,
          value: coerceRuleValueForOperator(
            condition.operator,
            coerceBlankBooleanRuleValue(
              sourceTypeFor?.(condition.source_scope, condition.source_field_key),
              condition.value,
            ),
          ),
        }));
      return { ...rule, conditions: effectiveConditions };
    })
    .filter((rule) => rule.conditions.length > 0)
    .map((rule) => {
      // Rücksprung ist nur bei genau einer Bedingung erlaubt (Backend-Validierung).
      if (Object.hasOwn(rule, "source_reset_value") && rule.conditions.length > 1) {
        const { source_reset_value: _removed, ...rest } = rule;
        return rest as AnswerRule;
      }
      return rule;
    });
}
